import React, { useState, useEffect } from 'react';
import { FolderOpen, Plus, X, Lock, ChevronUp, ChevronDown } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';

export default function ManageCategories({ organization, objectDefinitions, isOwner, onUpdate }) {
  const { showSuccess, showError } = useToast();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const config = organization?.settings?.navbar_manage_items_config;
    setCategories(config?.categories || ['CRM']);
  }, [organization?.settings]);

  const getObjectCountForCategory = (category) => {
    return objectDefinitions.filter((od) => (od.category || 'CRM') === category).length;
  };

  const saveCategories = async (newCategories) => {
    setSaving(true);
    try {
      const settings = {
        ...organization?.settings,
        navbar_manage_items_config: {
          ...organization?.settings?.navbar_manage_items_config,
          categories: newCategories,
        },
      };
      const response = await apiRequest('/organizations/current', {
        method: 'PUT',
        body: JSON.stringify({ settings }),
      });
      if (response.ok) {
        setCategories(newCategories);
        if (onUpdate) await onUpdate();
        showSuccess('Categories updated');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to update categories');
      }
    } catch (error) {
      showError('Error updating categories');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = () => {
    const name = newCategory.trim();
    if (!name) return;
    if (categories.some((c) => c.toLowerCase() === name.toLowerCase())) {
      showError('A category with that name already exists');
      return;
    }
    saveCategories([...categories, name]);
    setNewCategory('');
    setIsAdding(false);
  };

  const handleRemoveCategory = (index) => {
    const category = categories[index];
    const objectCount = getObjectCountForCategory(category);
    if (objectCount > 0) {
      showError(`Cannot remove "${category}" — ${objectCount} object${objectCount > 1 ? 's' : ''} assigned to it`);
      return;
    }
    saveCategories(categories.filter((_, i) => i !== index));
  };

  const handleMoveCategory = (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = index + direction;
    // Can't move before CRM (index 0) or beyond array
    if (targetIndex < 1 || targetIndex >= newCategories.length) return;
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    saveCategories(newCategories);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Manage Categories</h3>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">Organize objects in the sidebar</p>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-1">
          {categories.map((category, index) => {
            const isCRM = index === 0 && category === 'CRM';
            const objectCount = getObjectCountForCategory(category);
            return (
              <div
                key={`${category}-${index}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg group hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                {isCRM ? (
                  <Lock className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                ) : (
                  <div className="flex flex-col flex-shrink-0">
                    <button
                      onClick={() => handleMoveCategory(index, -1)}
                      disabled={index <= 1 || saving}
                      className="p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleMoveCategory(index, 1)}
                      disabled={index >= categories.length - 1 || saving}
                      className="p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{category}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {objectCount} object{objectCount !== 1 ? 's' : ''}
                </span>
                {!isCRM && isOwner && (
                  <button
                    onClick={() => handleRemoveCategory(index)}
                    disabled={saving}
                    className="p-1 rounded text-gray-300 hover:text-rose-500 dark:text-gray-600 dark:hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all disabled:cursor-not-allowed"
                    title={objectCount > 0 ? 'Remove objects first' : 'Remove category'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Add category */}
        {isOwner && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            {isAdding ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                    if (e.key === 'Escape') { setIsAdding(false); setNewCategory(''); }
                  }}
                  placeholder="Category name..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                  ref={(el) => { if (el) requestAnimationFrame(() => el.focus()); }}
                  disabled={saving}
                />
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategory.trim() || saving}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => { setIsAdding(false); setNewCategory(''); }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAdding(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg transition-colors w-full"
              >
                <Plus className="w-4 h-4" />
                <span>Add category</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
